import React, { useEffect, useState } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { AlertTriangle, Calculator } from 'lucide-react';
import { PRICE_UNIT_OPTIONS } from './constants';
import { LastQuoteChip } from './LastQuoteChip';
import { quotationsAPI } from '../../services/api';

export function PartItemForm({ selectedPart, selectedCosting, availableCostings, itemForm, setItemForm, onCostingChange, quotationId }) {
  const [lastQuote, setLastQuote] = useState(null);
  const update = (field, value) => setItemForm(p => ({ ...p, [field]: value }));
  const isKg = itemForm.quantity_unit === 'kg';
  const netWeightGrams = selectedPart?.net_weight_grams || selectedCosting?.net_weight_grams || 0;
  const kgRate = selectedCosting
    ? (selectedCosting.selling_price_per_kg
      || (netWeightGrams ? (selectedCosting.selling_price_per_piece * 1000) / netWeightGrams : 0))
    : 0;
  const impliedPieces = isKg && netWeightGrams
    ? Math.round(((itemForm.quantity || 0) * 1000) / netWeightGrams)
    : null;

  useEffect(() => {
    let cancelled = false;
    setLastQuote(null);
    if (!selectedPart?.id) return undefined;
    quotationsAPI.lastQuotes([selectedPart.id], quotationId)
      .then((res) => { if (!cancelled) setLastQuote(res.data.last_quotes?.[selectedPart.id] || null); })
      .catch(() => { });
    return () => { cancelled = true; };
  }, [selectedPart?.id, quotationId]);

  const changeUnit = (unit) => {    const kg = unit === 'kg';
    setItemForm(p => ({
      ...p,
      quantity_unit: unit,
      uom: kg ? 'kgs' : 'pcs',
      price_unit: kg ? 1 : p.price_unit,
      unit_price_override: kg
        ? (kgRate ? Number(kgRate.toFixed(4)) : p.unit_price_override)
        : (selectedCosting?.selling_price_per_piece || p.unit_price_override),
    }));
  };

  return (
    <div className="space-y-4">
      {/* Part Info */}
      <div className="p-3 bg-muted/50 rounded-lg">
        <div className="flex justify-between items-start">
          <div>
            <p className="font-mono font-bold">{selectedPart.internal_part_no}</p>
            <p className="text-sm text-muted-foreground">{selectedPart.description}</p>
          </div>
          {selectedPart.part_category === 'kit' && (
            <Badge className="bg-purple-100 text-purple-700">Kit</Badge>
          )}
        </div>
      </div>

      {/* Last quoted price for this part */}
      <div className="rounded-lg border bg-muted/30 p-3" data-testid="part-last-quote-panel">
        <p className="mb-1 text-xs font-medium uppercase text-muted-foreground">Last quoted for this part</p>
        <LastQuoteChip
          lastQuote={lastQuote}
          newRate={Number(itemForm.unit_price_override || 0)}
          testId="part-last-quote"
        />
      </div>

      {/* Costing Selector */}
      {availableCostings.length > 0 && (
        <div className="space-y-2 p-3 border rounded-lg bg-blue-50/50">
          <Label className="flex items-center gap-2">
            <Calculator className="h-4 w-4" />
            Select Cost Basis {availableCostings.length > 1 && '(Volume-Based Pricing)'}
          </Label>
          <Select value={selectedCosting?.id || ''} onValueChange={onCostingChange}>
            <SelectTrigger><SelectValue placeholder="Select a costing..." /></SelectTrigger>
            <SelectContent>
              {availableCostings.map(c => (
                <SelectItem key={c.id} value={c.id}>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs">{c.costing_number}</span>
                    <span className="text-muted-foreground">|</span>
                    <span>Qty: {c.quantity?.toLocaleString()}</span>
                    <span className="text-muted-foreground">|</span>
                    <span className="font-mono text-primary">
                      {c.selling_price_per_piece?.toFixed(2)}/pc
                      {c.selling_price_per_kg ? ` · ${c.selling_price_per_kg.toFixed(2)}/kg` : ''}
                    </span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">{availableCostings.length} costing(s) available. Price is editable below.</p>
        </div>
      )}

      {/* No Costing Warning */}
      {availableCostings.length === 0 && (
        <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-orange-600 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-orange-800">No Costing Available</p>
              <p className="text-xs text-orange-600">This part has no costing run. Enter the unit price manually below.</p>
            </div>
          </div>
        </div>
      )}

      {/* Pricing Section */}
      <div className="p-3 bg-primary/5 rounded-lg border border-primary/20">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">
              {isKg ? 'Rate per Kg (*)' : 'Unit Price (*)'}
            </Label>
            <Input type="number" step="0.01" value={itemForm.unit_price_override || ''} onChange={(e) => update('unit_price_override', parseFloat(e.target.value) || 0)} placeholder="0.00" className="font-mono" data-testid="unit-price-input" />
          </div>
          {!isKg && (
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Price Per</Label>
              <Select value={String(itemForm.price_unit)} onValueChange={(v) => update('price_unit', parseInt(v) || 1)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRICE_UNIT_OPTIONS.map(u => (
                    <SelectItem key={u.value} value={String(u.value)}>{u.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-2" data-testid="pricing-basis-hint">
          {isKg
            ? (kgRate
              ? `Base from costing: ${kgRate.toFixed(4)}/kg (${selectedCosting?.selling_price_per_piece?.toFixed(4)}/pc at ${netWeightGrams} g) — editable above`
              : 'Enter the rate per kg to quote for this item')
            : (selectedCosting ? `Base from costing: ${selectedCosting.selling_price_per_piece?.toFixed(4)}/pc — Editable above` : 'Enter the price to quote for this item')}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>{isKg ? 'Quantity (Kg) *' : 'Quantity (Pcs) *'}</Label>
          <Input type="number" value={itemForm.quantity} onChange={(e) => update('quantity', parseFloat(e.target.value) || 0)} data-testid="quantity-input" />
          {isKg && (
            <p className="text-xs text-muted-foreground" data-testid="implied-pieces-hint">
              {netWeightGrams
                ? `≈ ${impliedPieces.toLocaleString('en-IN')} pcs at ${netWeightGrams} g/pc`
                : 'Add a net weight on the part to see the implied piece count'}
            </p>
          )}
        </div>
        <div className="space-y-2">
          <Label>Unit</Label>
          <Select value={itemForm.quantity_unit} onValueChange={changeUnit}>
            <SelectTrigger data-testid="quantity-unit-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pcs">Pieces</SelectItem>
              <SelectItem value="kg">Kilograms</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Mfg Lead Time</Label>
          <Input value={itemForm.production_lead_time || ''} onChange={(e) => update('production_lead_time', e.target.value || null)} placeholder="e.g., 4-6 Weeks" />
        </div>
        <div className="space-y-2">
          <Label>Sample Lead Time</Label>
          <Input value={itemForm.sample_lead_time || ''} onChange={(e) => update('sample_lead_time', e.target.value || null)} placeholder="e.g., 2 Weeks" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Tooling Cost (One-Time)</Label>
          <Input type="number" value={itemForm.tooling_cost} onChange={(e) => update('tooling_cost', parseFloat(e.target.value) || 0)} />
        </div>
        <div className="space-y-2">
          <Label>Sample Cost (One-Time)</Label>
          <Input type="number" value={itemForm.sample_cost} onChange={(e) => update('sample_cost', parseFloat(e.target.value) || 0)} />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Remarks</Label>
        <Input value={itemForm.remarks} onChange={(e) => update('remarks', e.target.value)} />
      </div>
    </div>
  );
}
